import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatPhone } from '@/shared/lib/formatPhone';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchRepairReferral } from './repairReferralApi';

// Simplified vs legacy RepairReferral: the PDF export (html2canvas + jsPDF) and the RF Dewi brand styling are not
// migrated. The page prints cleanly instead — use «Скачать PDF» (browser print dialog -> «Сохранить как PDF»).

const SUPPORT_PHONE = { href: 'tel:+78003500507', text: '+7 (800) 350-05-07' };
const SUPPORT_TELEGRAM = { href: 'https://t.me/uremont_support', text: 't.me/uremont_support' };

const QR_HINT =
  'Это необходимо для определения автомобиля и услуг, закрепления скидки (при ее наличии), а также сохранения истории обслуживания автомобиля в личном кабинете UREMONT';

const Card = ({ title, children }: { title?: string; children: ReactNode }) => (
  <div className="box box-primary">
    <div className="box-body">
      {title && <h4 style={{ marginTop: 0 }}>{title}</h4>}
      {children}
    </div>
  </div>
);

export const RepairReferralPage = () => {
  const bidId = Number(useParams().id);
  const { data: referral, isLoading, error } = useQuery({
    queryKey: ['repair-referral', bidId],
    queryFn: () => fetchRepairReferral(bidId),
  });

  const station = referral?.service_station;
  const coordinates = station?.coordinates;
  const routeLink = `https://yandex.ru/maps/?rtext=~${coordinates?.lat}%2C${coordinates?.long}`;
  const discountPercent = Math.round(Number(referral?.discount?.applied_discount_percent));

  return (
    <>
      <section className="content-header">
        <h1>Направление в автосервис</h1>
      </section>
      <section className="content">
        <QueryStatus isLoading={isLoading} error={error} errorText="Не удалось загрузить направление" />
        {referral && !referral.bid_id && !isLoading && <p>Не удалось загрузить направление</p>}
        {referral?.bid_id && (
          <div style={{ maxWidth: 640 }}>
            <p>
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                Скачать PDF
              </button>
            </p>
            <Card>
              <h3 style={{ margin: 0 }}>Направление в автосервис</h3>
              <b>UREMONT</b> · заявка #{referral.bid_id}
            </Card>
            <Card title="Ваш автомобиль">
              <b>{`${referral.car?.mark ?? ''} ${referral.car?.model ?? ''}`.trim()}</b>
            </Card>
            <Card title="Выбранные услуги">
              <ul>
                {(referral.services ?? []).map(({ id, name }) => (
                  <li key={id}>{name}</li>
                ))}
              </ul>
            </Card>
            <Card title="Выбранный СТО">
              <div className="row">
                <div className="col-sm-8">
                  <div>{station?.name ?? ''} находится по адресу:</div>
                  {station?.address && <p>{station.address}</p>}
                  {station?.phone && (
                    <p>
                      <a href={`tel:${station.phone}`}>{formatPhone(station.phone)}</a>
                    </p>
                  )}
                  <a href={routeLink} target="_blank" rel="noopener noreferrer">
                    Проложить маршрут
                  </a>
                </div>
                <div className="col-sm-4">
                  {station?.photo_url && <img src={station.photo_url} alt="service_img" style={{ maxWidth: '100%' }} />}
                </div>
              </div>
            </Card>
            {discountPercent > 0 && (
              <Card title="Скидка от UREMONT">
                <b>{discountPercent}%</b> {referral.discount?.discount_name}
              </Card>
            )}
            {referral.qr?.image_url && (
              <Card>
                <div className="row">
                  <div className="col-sm-9">
                    <b>Покажите этот QR—код автосервису</b>
                    <p className="text-muted">{QR_HINT}</p>
                  </div>
                  <div className="col-sm-3">
                    <img src={referral.qr.image_url} alt="qr_code" style={{ maxWidth: '100%' }} />
                  </div>
                </div>
              </Card>
            )}
            <Card title="Возникли вопросы?">
              <p>
                Горячая линия поддержки UREMONT: <a href={SUPPORT_PHONE.href}>{SUPPORT_PHONE.text}</a>
              </p>
              <p>
                Telegram: <a href={SUPPORT_TELEGRAM.href}>{SUPPORT_TELEGRAM.text}</a>
              </p>
            </Card>
          </div>
        )}
      </section>
    </>
  );
};
